import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { AreaId } from '@shared/brand';
import { useSessionState } from './SessionProvider';

/**
 * Qué área están mirando las pantallas de gestión.
 *
 * Casi todo el portal pregunta por «mi área» y la respuesta es una sola: la de la
 * claim. Con el administrador global eso deja de bastar —su alcance son las
 * ocho—, y hacía falta una forma de decir *cuál* sin reescribir noventa y cuatro
 * consultas para que cada una supiera elegir.
 *
 * **Es foco, no permiso.** Cambiar el área enfocada no abre nada: quien no sea
 * `global` ni `super` recibe siempre su propia área de este hook, y aunque
 * lograra cambiarla, las reglas comparan la claim contra el documento y le
 * negarían la lectura. Esto sólo decide qué se pide, no qué se puede.
 *
 * **Vive en memoria y no en la URL.** Se pensó lo contrario —un `?area=` sería
 * compartible— y hay una razón para no hacerlo todavía: media docena de pantallas
 * tienen ya sus propios parámetros, y meter el área entre ellos obliga a decidir
 * qué pasa cuando alguien pega un enlace de un área que no puede ver. Un estado
 * de sesión no plantea esa pregunta. Si algún día hace falta compartir la vista,
 * ese es el trabajo, y es más grande que este.
 */
interface AreaFocus {
  /** El área que las pantallas de gestión deben consultar. */
  readonly areaId: AreaId;
  /** Si esta persona puede cambiarla. `global` y `super`, nadie más. */
  readonly canChange: boolean;
  readonly setAreaId: (areaId: AreaId) => void;
  /** Su propia área, para poder volver a ella. */
  readonly ownAreaId: AreaId;
}

const AreaFocusContext = createContext<AreaFocus | null>(null);

export const AreaFocusProvider = ({ children }: { readonly children: ReactNode }): ReactNode => {
  const { status } = useSessionState();
  const session = status.kind === 'ready' ? status.session : null;
  const [chosen, setChosen] = useState<AreaId | null>(null);

  const value = useMemo((): AreaFocus | null => {
    if (session === null) return null;
    const canChange = session.role === 'global' || session.role === 'super';
    return {
      /* Sin elección, la propia — que es lo que hacía el portal entero antes de
         que esto existiera, y sigue siendo lo correcto para los otros cuatro
         roles. */
      areaId: canChange && chosen !== null ? chosen : session.areaId,
      canChange,
      ownAreaId: session.areaId,
      setAreaId: setChosen,
    };
  }, [session, chosen]);

  return <AreaFocusContext.Provider value={value}>{children}</AreaFocusContext.Provider>;
};

/**
 * El área que toca consultar.
 *
 * Sustituye a `session.areaId` en las pantallas de gestión —calendario, reportes,
 * personas, bitácora— y **no** en las personales: «Mis ausencias» o «Mi perfil»
 * son de quien mira, y seguirlas al foco convertiría un cambio de vista en un
 * cambio de identidad.
 *
 * Fuera del proveedor devuelve la propia área, para que un componente montado
 * suelto —una prueba, una vista previa— no reviente.
 */
export const useScopedAreaId = (fallback: AreaId): AreaId =>
  useContext(AreaFocusContext)?.areaId ?? fallback;

/** El control que cambia el foco. `null` fuera del proveedor o sin sesión. */
export const useAreaFocus = (): AreaFocus | null => useContext(AreaFocusContext);
